// Guard de trial + simultaneidade (sub-onda εd) — central pros players.
// Abre uma stream_session no backend ao montar, bate heartbeat periódico, e
// expõe o estado. Quando o trial esgota DURANTE o vídeo (heartbeat → blocked),
// `blocked` vira true e o player interrompe + mostra a tela de compra.
//
// Erros no start: 'stream_limit' (limite de telas do plano) | 'trial_blocked'
// (trial já esgotado hoje). O player decide qual overlay mostrar.

import { useEffect, useRef, useState } from 'react'

import { api } from '../api'

export type TrialBlockReason = null | 'stream_limit' | 'trial_blocked'

interface TrialGuardState {
  /** true quando o conteúdo deve ser interrompido (trial esgotou agora). */
  blocked: boolean
  /** motivo do bloqueio INICIAL (no start) — null se abriu normal. */
  startBlock: TrialBlockReason
  /** segundos restantes do trial hoje (null = ilimitado/pago). */
  remainingSeconds: number | null
  ready: boolean
}

const HEARTBEAT_MS = 30_000
// Após N heartbeats falhos seguidos, bloqueia (fail-closed) — anti-bypass.
const MAX_HEARTBEAT_FAILS = 3

/** Mapeia erro do start pro motivo do overlay (puro — testável sem DOM).
 *  ApiError expõe `code` (do body {error}) e `status` — NÃO `body`. */
export function mapStartError(err: unknown): TrialBlockReason {
  const status = (err as { status?: number })?.status
  const code = (err as { code?: string })?.code
  if (status === 409 || code === 'stream_limit_reached') return 'stream_limit'
  if (status === 402 || code === 'trial_exhausted') return 'trial_blocked'
  return null
}

export function useTrialGuard(active: boolean): TrialGuardState {
  const [state, setState] = useState<TrialGuardState>({
    blocked: false,
    startBlock: null,
    remainingSeconds: null,
    ready: false,
  })
  const sessionIdRef = useRef<string | null>(null)
  const failsRef = useRef(0)

  useEffect(() => {
    if (!active) return
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null
    failsRef.current = 0

    async function startGuard(): Promise<void> {
      try {
        const res = await api.streamSessionStart()
        if (cancelled) {
          // já desmontou — encerra a sessão recém-criada
          void api.streamSessionEnd(res.streamSessionId)
          return
        }
        sessionIdRef.current = res.streamSessionId
        setState({
          blocked: res.phase === 'blocked',
          startBlock: null,
          remainingSeconds: res.remainingSeconds,
          ready: true,
        })

        timer = setInterval(() => {
          const sid = sessionIdRef.current
          if (!sid) return
          void api
            .streamSessionHeartbeat(sid)
            .then((hb) => {
              if (cancelled) return
              failsRef.current = 0
              setState((s) => ({
                ...s,
                blocked: hb.phase === 'blocked',
                remainingSeconds: hb.remainingSeconds,
              }))
            })
            .catch(() => {
              if (cancelled) return
              // FAIL-CLOSED: heartbeat falhando N vezes seguidas → bloqueia.
              // Senão derrubar a rede (ou bloquear nosso domínio) = trial
              // infinito (vetor de bypass anti-fraude). N×30s de tolerância
              // cobre instabilidade transitória sem punir o usuário à toa.
              failsRef.current += 1
              if (failsRef.current >= MAX_HEARTBEAT_FAILS) {
                setState((s) => ({ ...s, blocked: true }))
              }
            })
        }, HEARTBEAT_MS)
      } catch (err) {
        if (cancelled) return
        setState({ blocked: false, startBlock: mapStartError(err), remainingSeconds: 0, ready: true })
      }
    }

    void startGuard()

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      const sid = sessionIdRef.current
      if (sid) {
        void api.streamSessionEnd(sid)
        sessionIdRef.current = null
      }
    }
  }, [active])

  return state
}
